import { buildLiveSafetyCasePath } from '../../utils/acceptanceHierarchyNavigation';
import { resolveOverviewPlaceLabel } from './safetyCaseOverviewPresenter';
import { buildSignedRecordPath, coerceRegisterRevision } from './workspaceRegisters';
import { buyerConditionDisplayName } from '../buyerFacing/conditionDisplayNames';

function asText(value: unknown): string {
  return String(value || '').trim();
}

function humanizeToken(value: unknown): string {
  return asText(value).replace(/[_-]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function looksLikeRawWorkflowId(value: string): boolean {
  return value.includes('|') || /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
}

export function buildSafetyOverviewReturnPath(siteId?: string | null): string | null {
  const site = asText(siteId);
  if (!site) return null;
  return `/acceptance/overview-v2?site=${encodeURIComponent(site)}`;
}

export type SignedRecordRevisionSource = {
  latestScanCurrentRevision?: unknown;
  scanHistory?: Array<{
    report_id?: string | null;
    current_revision?: unknown;
    revisions?: Array<{ revision?: number | null }> | null;
  }> | null;
  finalizedReportId?: string | null;
  reportId?: string | null;
};

export function resolveCaseSignedRecordIdentity(input: SignedRecordRevisionSource): {
  recordId: string | null;
  revision: number | null;
} {
  const requested = asText(input.reportId);
  const latestFinalized = asText(input.finalizedReportId);
  const recordId = requested || latestFinalized || null;
  if (!recordId) return { recordId: null, revision: null };
  const matching = (input.scanHistory || []).find((row) => asText(row.report_id) === recordId) || null;
  const allowLatestPointer = !requested || requested === latestFinalized;
  const revision = coerceRegisterRevision({
    revision: (allowLatestPointer
      ? (input.latestScanCurrentRevision as number | string | null | undefined)
      : undefined)
      ?? matching?.current_revision as number | string | null | undefined,
    revisions: matching?.revisions || null,
  });
  return { recordId, revision };
}

/**
 * Canonical signed-record route requires record ID + revision.
 * Backend signed-control-record treats record_id as the finalized report_id.
 * Never emit `/acceptance/reports/records/:id` without a revision.
 */
export function resolveSignedSafetyControlRecordHref(input: {
  recordId?: string | null;
  reportId?: string | null;
  revision?: number | string | null;
  revisions?: Array<{ revision?: number | null }> | null;
  orgId?: string | null;
  siteId?: string | null;
}): string | null {
  const recordId = asText(input.recordId || input.reportId);
  const revision = coerceRegisterRevision({
    revision: input.revision,
    revisions: input.revisions,
  });
  if (!recordId || revision == null) return null;
  const query = new URLSearchParams();
  if (asText(input.orgId)) query.set('org_id', asText(input.orgId));
  if (asText(input.siteId)) query.set('site_id', asText(input.siteId));
  return buildSignedRecordPath({ reportId: recordId, revision }, query);
}

export function resolveIncidentWorkflowProblemContext(input: {
  workflowId?: string | null;
  groupKey?: string | null;
  conditionId?: string | null;
  cameraId?: string | null;
  zoneId?: string | null;
  incidentType?: string | null;
}): { place: string; problem: string } | null {
  const workflow = asText(input.workflowId || input.groupKey);
  if (!workflow && !asText(input.conditionId)) return null;
  const parts = workflow.split('|').map((part) => part.trim()).filter(Boolean);
  const offset = parts.length >= 5 ? 1 : 0;
  const incidentType = asText(input.incidentType) || parts[offset] || '';
  const zoneId = asText(input.zoneId) || parts[offset + 1] || '';
  const cameraId = asText(input.cameraId) || parts[offset + 2] || '';
  const place = resolveOverviewPlaceLabel({ cameraId, zoneId });
  const problem = buyerConditionDisplayName(humanizeToken(incidentType), incidentType);
  if (!place && !problem) return null;
  if (looksLikeRawWorkflowId(place) || looksLikeRawWorkflowId(problem)) return null;
  return {
    place: place || 'Monitored area',
    problem: problem || 'Safety pattern',
  };
}

export function resolveSignedScanArtifactReturnPaths(input: {
  caseId?: string | null;
  findingId?: string | null;
  artifactFindingId?: string | null;
  querySafetyCaseId?: string | null;
  queryCaseId?: string | null;
  orgId?: string | null;
  siteId?: string | null;
  workflowId?: string | null;
  incidentId?: string | null;
  zoneId?: string | null;
  cameraId?: string | null;
  deviceId?: string | null;
  policyType?: string | null;
  recommendedNextStep?: string | null;
}): { liveCasePath: string; livePatternPath: string; liveWorkflowPath: string } {
  const caseId = asText(input.caseId || input.findingId || input.artifactFindingId || input.querySafetyCaseId || input.queryCaseId);
  const siteId = asText(input.siteId);
  const workflowId = asText(input.workflowId);
  const liveCasePath = caseId
    ? buildLiveSafetyCasePath({
      caseId,
      orgId: asText(input.orgId) || undefined,
      siteId: siteId || undefined,
      workflowId: workflowId || undefined,
      incidentId: asText(input.incidentId) || undefined,
    })
    : '';
  let livePatternPath = '';
  if (workflowId && siteId) {
    const query = new URLSearchParams();
    query.set('site_id', siteId);
    query.set('tab', 'open');
    query.set('table_mode', 'grouped');
    query.set('review', '1');
    query.set('workflow_id', workflowId);
    if (asText(input.zoneId)) query.set('zone_id', asText(input.zoneId));
    if (asText(input.cameraId)) query.set('camera_id', asText(input.cameraId));
    if (asText(input.deviceId)) query.set('device_id', asText(input.deviceId));
    if (asText(input.policyType)) query.set('policy_type', asText(input.policyType));
    if (asText(input.incidentId)) query.set('incident_id', asText(input.incidentId));
    const nextStep = asText(input.recommendedNextStep).toLowerCase();
    if (nextStep === 'complete_evidence_review') query.set('review_focus', 'evidence_review');
    if (nextStep === 'complete_corrective_action') query.set('review_focus', 'corrective_action');
    if (nextStep === 'complete_impact_measurement') query.set('review_focus', 'impact_measurement');
    livePatternPath = `/incidents?${query.toString()}`;
  }
  return {
    liveCasePath,
    livePatternPath,
    liveWorkflowPath: liveCasePath || livePatternPath,
  };
}
